import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { 
  X, 
  MessageSquare, 
  Send, 
  Mail, 
  AlertTriangle, 
  Check,
  MailOpen,
  Clock,
  User
} from 'lucide-react';

const ContactDialog = ({ 
  contactDialogOpen, 
  setContactDialogOpen, 
  selectedContact, 
  contactResponse, 
  setContactResponse, 
  isLoading,
  setIsLoading,
  showNotification,
  logAction,
  currentUser,
  refreshList
}) => {
  // State for previous responses (if any)
  const [previousResponses, setPreviousResponses] = useState([]);
  const [responderInfo, setResponderInfo] = useState(null);
  
  // Load previous responses when dialog opens
  useEffect(() => {
    if (contactDialogOpen && selectedContact?.id) {
      loadPreviousResponses();
    }
  }, [contactDialogOpen, selectedContact]);
  
  // Function to load previous responses
  const loadPreviousResponses = async () => {
    if (!selectedContact || !selectedContact.respondido) return;
    
    try {
      const response = await axios.get(`/api/admin/contacts/${selectedContact.id}/responses`);
      setPreviousResponses(response.data.responses || []);
      
      // If there's a responder ID, get their info
      if (response.data.responses && response.data.responses.length > 0 && response.data.responses[0].id_respondedor) {
        try {
          const staffResponse = await axios.get(`/api/admin/staff/${response.data.responses[0].id_respondedor}`);
          setResponderInfo(staffResponse.data);
        } catch (error) {
          console.error('Error al obtener información del respondedor:', error);
        }
      }
    } catch (error) {
      console.error('Error al cargar respuestas previas:', error);
      showNotification('Error al cargar respuestas previas', 'error');
    }
  };
  
  // Function to format message type
  const formatMessageType = (type) => {
    switch(type) {
      case 'consulta': return 'Consulta';
      case 'retroalimentacion': return 'Retroalimentación';
      case 'reporte': return 'Reporte';
      case 'otro': return 'Otro';
      default: return type;
    }
  };
  
  // Function to get severity class based on message type
  const getTypeClass = (type) => {
    switch(type) {
      case 'consulta': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'retroalimentacion': return 'bg-green-100 text-green-800 border-green-200';
      case 'reporte': return 'bg-red-100 text-red-800 border-red-200';
      case 'otro': return 'bg-purple-100 text-purple-800 border-purple-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };
  
  // Function to process the response
  const handleSubmitResponse = async () => {
    if (!selectedContact || !contactResponse.trim()) return;
    
    setIsLoading(true);
    try {
      const response = await axios.post(`/api/admin/contacts/${selectedContact.id}/respond`, {
        response: contactResponse,
        staff_id: currentUser.id
      });
      
      showNotification('Respuesta enviada correctamente', 'success');
      
      // Log action
      logAction(
        'CONTACTO_RESPONDIDO',
        `Se ha respondido al contacto #${selectedContact.id}`,
        selectedContact.id,
        'contact'
      );
      
      // Close dialog
      setContactDialogOpen(false);
      
      // Refresh the list to show updated status
      refreshList();
      
    } catch (error) {
      console.error('Error al enviar respuesta:', error);
      showNotification(`Error al enviar respuesta: ${error.response?.data?.message || error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {contactDialogOpen && (
        <motion.div
          className="fixed inset-0 overflow-y-auto z-50 flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setContactDialogOpen(false)} />
          
          <motion.div
            className="relative bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: 'spring', damping: 30 }}
          >
            <div className="px-6 py-4 bg-indigo-600 text-white flex justify-between items-center">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                <h3 className="text-lg font-medium">
                  {selectedContact?.respondido ? 'Detalle de Contacto Respondido' : 'Responder Contacto'}
                </h3>
                {selectedContact && (
                  <div className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                    selectedContact.respondido ? 'bg-green-500/20 text-green-100' : 'bg-amber-500/20 text-amber-100'
                  }`}>
                    {selectedContact.respondido ? 'Respondido' : 'Pendiente'}
                  </div>
                )}
              </div>
              <button 
                onClick={() => setContactDialogOpen(false)}
                className="p-1 rounded-full hover:bg-white/10 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {selectedContact && (
              <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
                <div className="p-6">
                  {/* Contact Info */}
                  <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 mb-6">
                    <div className="flex justify-between mb-4">
                      <h4 className="text-md font-semibold text-indigo-800 flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        Información del Contacto
                      </h4>
                      
                      <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-lg ${getTypeClass(selectedContact.tipo_mensaje)}`}>
                        <span className="text-sm font-medium">
                          {formatMessageType(selectedContact.tipo_mensaje)}
                        </span>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-xs font-medium text-gray-500">Nombre</p>
                        <p className="text-sm text-gray-700 flex items-center gap-1.5">
                          <User className="w-3.5 h-3.5 text-indigo-500" />
                          {selectedContact.nombre}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500">Email</p>
                        <p className="text-sm text-gray-700 flex items-center gap-1.5">
                          <Mail className="w-3.5 h-3.5 text-indigo-500" />
                          {selectedContact.email}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500">Teléfono</p>
                        <p className="text-sm text-gray-700">
                          {selectedContact.telefono || 'No proporcionado'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500">Fecha de Envío</p>
                        <p className="text-sm text-gray-700 flex items-center gap-1.5">
                          <Clock className="w-3.5 h-3.5 text-indigo-500" />
                          {new Date(selectedContact.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    
                    <div className="mb-4">
                      <p className="text-xs font-medium text-gray-500">Asunto</p>
                      <p className="text-sm font-medium text-gray-800">{selectedContact.asunto}</p>
                    </div>
                    
                    <div>
                      <p className="text-xs font-medium text-gray-500 mb-2">Mensaje</p>
                      <div className="bg-gray-50 border border-gray-100 rounded-lg p-4">
                        <p className="text-sm text-gray-700 whitespace-pre-line">
                          {selectedContact.mensaje}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Previous Response (if responded) */}
                  {selectedContact.respondido && previousResponses.length > 0 && (
                    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 mb-6">
                      <h4 className="text-md font-semibold text-indigo-800 mb-4 flex items-center gap-2">
                        <MailOpen className="w-4 h-4" />
                        Respuesta Enviada
                      </h4>
                      
                      <div className="bg-indigo-50/50 border border-indigo-100 rounded-lg p-4 mb-3">
                        <p className="text-sm text-gray-700 whitespace-pre-line">
                          {previousResponses[0].respuesta}
                        </p>
                      </div>
                      
                      <div className="text-xs text-gray-500 flex justify-between">
                        <span className="flex items-center gap-1">
                          <User className="w-3.5 h-3.5" />
                          Respondido por: {responderInfo?.nombre || `ID: ${previousResponses[0].id_respondedor}` || 'Desconocido'}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          {new Date(previousResponses[0].created_at).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  )}
                  
                  {/* Form to respond (only if not already responded) */}
                  {!selectedContact.respondido && (
                    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
                      <h4 className="text-md font-semibold text-indigo-800 mb-4 flex items-center gap-2">
                        <Send className="w-4 h-4" />
                        Enviar Respuesta
                      </h4>
                      
                      <textarea
                        value={contactResponse}
                        onChange={(e) => setContactResponse(e.target.value)}
                        placeholder="Escribe tu respuesta aquí..."
                        className="w-full px-4 py-3 bg-indigo-50/50 border border-indigo-100 rounded-lg text-gray-700 
                                placeholder-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        rows={6}
                      />
                      
                      <div className="mt-3 bg-blue-50 p-3 rounded-lg border border-blue-100 flex items-start gap-2">
                        <AlertTriangle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-gray-700">
                          <p className="font-medium text-blue-800 mb-1">Información</p>
                          <p>Tu respuesta será enviada al email del contacto ({selectedContact.email}) y quedará registrada en el sistema.</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
                  <motion.button
                    onClick={() => setContactDialogOpen(false)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Cerrar
                  </motion.button>
                  
                  {!selectedContact.respondido && (
                    <motion.button
                      onClick={handleSubmitResponse}
                      className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-lg shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      disabled={!contactResponse.trim() || isLoading}
                    >
                      {isLoading ? (
                        <span className="inline-block w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></span>
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                      Enviar Respuesta
                    </motion.button>
                  )}
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ContactDialog;